import { useContext, useMemo } from "react";
import { Query, QueryClientContext } from "@tanstack/react-query";
import useAPI from "@/services/api/hook";
import {
  ACCOUNT_MODE_SETTING_KEY,
  SANDBOX_ACCOUNT_MODE
} from "@/services/auth/activeAccount";
import { AccountSetting } from "@/entities/settings/types";
import { isUnsetAccountSettingError } from "@/entities/settings/accountSettingDefaults";

const ACCOUNT_MODE_ENDPOINT = "settings/account";

// A live account has no stored `account_mode` row, so the read answers 404.
// A failed query holds no data, so `staleTime` alone doesn't stop React Query
// from fetching it again on every mount, focus or reconnect. Each navigation
// remounts several readers, and each refetch logged another 404. A 404 is a
// real answer ("live"), so it is kept for the session. Any other error says
// nothing about the mode, so it still refetches on mount, focus and reconnect.
// Switching accounts clears the whole query cache, so the next account still
// gets its own read.
const refetchUnlessUnset = (query: Pick<Query, "state">) =>
  !isUnsetAccountSettingError(query.state.error);

/**
 * Whether the active account is a sandbox, read from its own `account_mode`
 * setting with the active tenant. A child account in "live" mode, a missing
 * setting, or one that can't be read is not a sandbox.
 */
export const useIsSandboxAccount = (): boolean => {
  // Read straight from the context so callers that stub `useAPI` (and render
  // without a provider) still work; `useQueryClient` throws there.
  const queryClient = useContext(QueryClientContext);
  const options = useMemo(
    () => ({ pathParams: { settingKey: ACCOUNT_MODE_SETTING_KEY } }),
    []
  );
  // `retryOnMount` takes no function, so read the cached answer here: a 404
  // already on record is not fetched again when another reader mounts.
  const cachedError = queryClient
    ?.getQueryCache()
    .find({ queryKey: [ACCOUNT_MODE_ENDPOINT, options], exact: false })
    ?.state.error;
  const { data } = useAPI(ACCOUNT_MODE_ENDPOINT, options, {
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: !isUnsetAccountSettingError(cachedError),
    refetchOnWindowFocus: refetchUnlessUnset,
    refetchOnReconnect: refetchUnlessUnset
  });
  const mode = (data as AccountSetting | undefined)?.value;
  return (
    typeof mode === "string" &&
    mode.trim().toLowerCase() === SANDBOX_ACCOUNT_MODE
  );
};
